import { type DateRange } from "react-day-picker";

import { dateToLocalISO, parseISO } from "./date-range-from-preset";

export interface DateRangeLabels {
    custom?: string;
    from?: string;
    to?: string;
}

export interface DisplayLabelInput {
    value: string | undefined;
    isCustom: boolean;
    presetKey: string;
    labels: DateRangeLabels | undefined;
    localizedPreset: (key: string) => string;
    customFallback: string;
}

/** Human label for the filter trigger. `localizedPreset` and `customFallback`
 * carry the i18n strings so this helper stays pure and testable. */
export function formatDateRangeDisplayLabel({
    value,
    isCustom,
    presetKey,
    labels,
    localizedPreset,
    customFallback,
}: DisplayLabelInput): string {
    if (isCustom) {
        const [from, to] = (value ?? "").slice(7).split(":");
        if (from && to) return `${from} — ${to}`;
        if (from) return `${labels?.from ?? "From"} ${from}`;
        if (to) return `${labels?.to ?? "To"} ${to}`;
        return labels?.custom ?? customFallback;
    }
    if (presetKey) return localizedPreset(presetKey);
    return "—";
}

/** Parses a stored "custom:from:to" value into the calendar's pending range. */
export function extractPendingRange(value: string | undefined): DateRange | undefined {
    if (!value?.startsWith("custom:")) return undefined;
    const [fromStr, toStr] = value.slice(7).split(":");
    const from = parseISO(fromStr);
    const to = parseISO(toStr);
    return from ? { from, to } : undefined;
}

/** Builds the stored "custom:from:to" value from the calendar selection. */
export function buildCustomRangeValue(range: DateRange | undefined): string {
    const from = range?.from ? dateToLocalISO(range.from) : "";
    const to = range?.to ? dateToLocalISO(range.to) : "";
    return `custom:${from}:${to}`;
}

/** Select-trigger value: a sentinel for custom/all, otherwise the preset key. */
export function resolveSelectValue(isCustom: boolean, presetKey: string): string {
    return isCustom ? "__custom__" : presetKey || "__all__";
}

export type SelectChange =
    { action: "clear" } | { action: "custom" } | { action: "preset"; presetValue: string };

/** Maps a raw Select value to the intent (clear / open custom picker / pick preset). */
export function classifySelectChange(value: string): SelectChange {
    if (value === "__all__") return { action: "clear" };
    if (value === "__custom__") return { action: "custom" };
    return { action: "preset", presetValue: value };
}
